import React, { useState } from 'react';
import { SheetProcessingRecord } from '../../types/generic';
import {
	Button,
	Checkbox,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	FormGroup,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { genericSheetOptions, specialSheetOptions, subheaders } from '../../utils/sheets';

interface SheetSelectorProps {
	isOpen: boolean;
	setOpen: (open: boolean) => void;
	sheets: SheetProcessingRecord;
	updateSheets: (newSheets: SheetProcessingRecord) => void;
}

const SheetSelector: React.FC<SheetSelectorProps> = ({ isOpen, setOpen, sheets, updateSheets }) => {
	const [newSheets, setNewSheets] = useState<SheetProcessingRecord>(sheets);
	const [genericSheetsOpen, setGenericSheetsOpen] = useState<boolean>(true);

	const updateSheet = (sheetName: string, checked: boolean) => {
		setNewSheets({
			...newSheets,
			[sheetName]: checked,
		});
	};

	const bulkUpdate = (group: 'special' | 'generic', checked: boolean) => {
		const update = { ...newSheets };
		if (group === 'special') {
			specialSheetOptions.forEach((opt) => {
				update[opt] = checked;
			});
		} else if (group === 'generic') {
			genericSheetOptions.forEach((opt) => {
				update[opt] = checked;
			});
		}

		setNewSheets(update);
	};

	const handleEnter = () => {
		setNewSheets(sheets);
	};

	return (
		<Dialog
			open={isOpen}
			onClose={() => setOpen(false)}
			data-testid="sheet-selector-dialog"
			closeAfterTransition={false}
			slotProps={{ transition: { onEnter: handleEnter } }}
		>
			<DialogTitle>Select Sheets</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<Tooltip title="Sheets with specialised processing methods">
						<Typography variant="h6" component={'h3'} textTransform={'none'} width={'100%'} padding={1}>
							Special Sheets
						</Typography>
					</Tooltip>
					<FormGroup row>
						{specialSheetOptions.map((opt) => (
							<FormControlLabel
								control={
									<Checkbox
										data-testid={`${opt}-special-checkbox`}
										checked={newSheets[opt]}
										onChange={(_, checked) => updateSheet(opt, checked)}
									/>
								}
								label={opt}
								key={`${opt}-special-checkbox`}
							/>
						))}
					</FormGroup>

					<Stack direction={'row-reverse'} gap={2}>
						<Button variant="contained" data-testid="deselect-all-special-sheets-button" onClick={() => bulkUpdate('special', false)}>
							Deselect All
						</Button>
						<Button variant="contained" data-testid="select-all-special-sheets-button" onClick={() => bulkUpdate('special', true)}>
							Select All
						</Button>
					</Stack>

					<Tooltip title={`Sheets with best-effort processing. Click to ${genericSheetsOpen ? 'shrink' : 'expand'} list`}>
						<Button fullWidth onClick={() => setGenericSheetsOpen(!genericSheetsOpen)}>
							<Stack direction={'row'} justifyContent={'space-between'} alignItems="center" width={'100%'}>
								<Typography variant="h6" component="h3" textTransform={'none'}>
									Generic Sheets
								</Typography>
								{genericSheetsOpen ? <ExpandLess /> : <ExpandMore />}
							</Stack>
						</Button>
					</Tooltip>
					<Collapse in={genericSheetsOpen}>
						<FormGroup>
							{subheaders.map((letter) => {
								return (
									<React.Fragment key={`special-sheets-subheader-${letter}`}>
										<Typography variant="subtitle1" component={'h4'}>
											{letter}
										</Typography>
										<Divider />
										<FormGroup row>
											{genericSheetOptions.map(
												(opt) =>
													opt.startsWith(letter) && (
														<FormControlLabel
															control={
																<Checkbox
																	data-testid={`${opt}-generic-checkbox`}
																	checked={newSheets[opt]}
																	onChange={(_, checked) => updateSheet(opt, checked)}
																/>
															}
															label={opt}
															key={`${opt}-generic-checkbox`}
														/>
													)
											)}
										</FormGroup>
									</React.Fragment>
								);
							})}
						</FormGroup>
					</Collapse>

					<Stack direction={'row-reverse'} gap={2}>
						<Button variant="contained" data-testid="deselect-all-generic-sheets-button" onClick={() => bulkUpdate('generic', false)}>
							Deselect All
						</Button>
						<Button variant="contained" data-testid="select-all-generic-sheets-button" onClick={() => bulkUpdate('generic', true)}>
							Select All
						</Button>
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button data-testid="sheet-selector-dialog-cancel" onClick={() => setOpen(false)}>
					Cancel
				</Button>
				<Button
					data-testid="sheet-selector-dialog-confirm"
					onClick={() => {
						updateSheets(newSheets);
						setOpen(false);
					}}
				>
					Confirm
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SheetSelector;
